import React from "react";
import { useTranslation } from "react-i18next";
import UserAvatar from "./common/UserAvatar";
import { MeetingType } from "../services/meetingTypeService";
import { PublicProvider } from "../types/product";

// Extended interface to handle both HostInfo and User types
interface ExtendedHostData {
  id?: string | number;
  name: string;
  email: string;
  profilePicture?: string;
  jobTitle?: string;
  title?: string;
  department?: string;
  company?: string;
  organizationName?: string;
  bio?: string; // Host bio/description for service type duration mode
}

interface HostDisplayProps {
  meetingType: MeetingType;
  position?: 'above_title' | 'below_description' | 'end_of_details';
  className?: string;
  /** Selected provider for service type - used to display provider-specific info */
  selectedProvider?: PublicProvider | null;
}

// Maximum avatars to show before displaying +N counter
const MAX_VISIBLE_AVATARS = 3;

/**
 * HostDisplay Component
 *
 * Displays host information based on meeting type's hostDisplaySettings
 * Supports multiple display modes: host details, company only, or hidden
 * For multi-host (collective) meetings, shows multiple avatars with +N counter
 */
const HostDisplay: React.FC<HostDisplayProps> = ({
  meetingType,
  position = 'above_title',
  className = '',
  selectedProvider
}) => {
  const { t } = useTranslation();

  // Check if this is a service type meeting
  const isServiceType = meetingType.type === 'service';

  // Get host display settings with comprehensive defaults
  // This ensures the component works even if the backend doesn't have hostDisplaySettings yet
  const settings = meetingType.hostDisplaySettings || {
    showHostDetails: 'host' as const,
    detailsToShow: ['name', 'description', 'title', 'department', 'company'],
    displayPosition: 'above_title' as const,
    multiHostAvatarMode: 'multiple' as const // Default to show all avatars for multi-host
  };

  // Per-meeting-type display overrides. A non-empty override replaces the real
  // host/org value on THIS booking page only — it never reflects a change to the
  // underlying organization or user record. Empty/absent = use the real value.
  const detailOverrides = meetingType.hostDisplaySettings?.detailOverrides || {};
  const applyOverride = (
    field: 'name' | 'title' | 'department' | 'company',
    realValue?: string
  ): string | undefined => {
    const override = detailOverrides[field];
    return override && override.trim() !== '' ? override : realValue;
  };
  // True when an override exists for a field even if the host has no real value —
  // used so `shouldShow` still renders a field the host chose to customize.
  const hasOverride = (field: 'name' | 'title' | 'department' | 'company') =>
    !!(detailOverrides[field] && detailOverrides[field]!.trim() !== '');

  // Only show if the position matches and not set to 'none'
  if (settings.displayPosition !== position || settings.showHostDetails === 'none') {
    return null;
  }

  // Check if this is a round-robin meeting type
  // Round Robin should ALWAYS use company mode (host is dynamically assigned, not known at booking time)
  const isRoundRobin = meetingType.type === 'round_robin';

  // Check if this is a multi-host meeting type with multiple hosts
  // Both collective (Multi-Host), event (Event Booking), and round_robin types support multiple hosts
  const isMultiHostMeeting = meetingType.type === 'collective' || meetingType.type === 'event' || isRoundRobin;
  const allHosts = meetingType.hosts || [];
  const hasMultipleHosts = allHosts.length > 1;

  // Determine if we should show multiple avatars
  // Only for multi-host meetings with "Host's Profile Picture" selected and "Show all hosts" mode
  // When "Company Logo" is selected, we show the logo instead of avatars
  // Round Robin NEVER shows multiple avatars (should use company mode)
  const showMultipleAvatars = !isRoundRobin && isMultiHostMeeting && hasMultipleHosts &&
    settings.showHostDetails === 'host' &&
    (settings.multiHostAvatarMode !== 'single');

  // For round_robin, force company mode display (host is dynamically assigned)
  // This ensures consistent display even for meeting types created before this fix
  const effectiveShowHostDetails = isRoundRobin ? 'company' : settings.showHostDetails;

  // Get host data (prioritize host, then primary from hosts array, then createdBy, then user)
  // This ensures we get department/title data from the hosts array when available
  const primaryHostFromArray = allHosts.find(h => h.role === 'primary') || allHosts[0];
  const hostData = meetingType.host || primaryHostFromArray || meetingType.createdBy || meetingType.user;

  if (!hostData && !hasMultipleHosts) {
    return null;
  }

  // For multi-host display with multiple avatars
  if (showMultipleAvatars) {
    const visibleHosts = allHosts.slice(0, MAX_VISIBLE_AVATARS);
    const remainingCount = allHosts.length - MAX_VISIBLE_AVATARS;
    const selectedDepartment = settings.selectedDepartmentValue;
    const detailsToShow = settings.detailsToShow || ['name', 'description', 'title', 'department', 'company'];

    // Get company name from primary host or meeting type user (override wins)
    const primaryHost = allHosts.find(h => h.role === 'primary') || allHosts[0];
    const companyName = applyOverride('company', primaryHost?.organizationName || meetingType.user?.organizationName);
    const showCompany = detailsToShow.includes('company') && companyName;

    return (
      <div className={`flex items-center ${className}`}>
        {/* Stacked Avatars */}
        <div className="flex -space-x-2">
          {visibleHosts.map((host, index) => (
            <div
              key={host.id || index}
              className="relative ring-2 ring-white rounded-full"
              style={{ zIndex: MAX_VISIBLE_AVATARS - index }}
            >
              <UserAvatar
                user={host}
                size="md"
                showProfilePicture={true}
              />
            </div>
          ))}
          {/* +N counter if more hosts than visible */}
          {remainingCount > 0 && (
            <div
              className="relative flex items-center justify-center w-10 h-10 rounded-full bg-gray-200 text-gray-600 text-xs font-medium ring-2 ring-white"
              style={{ zIndex: 0 }}
              title={t('meetingTypes:types.multiHost.hostDisplay.avatarLimit', '+{{count}} more', { count: remainingCount })}
            >
              +{remainingCount}
            </div>
          )}
        </div>
        {/* Show selected department and/or company if configured */}
        {(selectedDepartment || showCompany) && (
          <div className="ml-3">
            {selectedDepartment && (
              <span className="text-xs text-gray-600">
                {selectedDepartment}
              </span>
            )}
            {selectedDepartment && showCompany && (
              <span className="text-xs text-gray-400 mx-1">•</span>
            )}
            {showCompany && (
              <span className="text-xs text-gray-500">
                {companyName}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  // Type cast to extended interface for safe access
  const extendedHostData = hostData as ExtendedHostData;

  // Company logo & details display
  // For round_robin, this is always true (forced to company mode)
  if (effectiveShowHostDetails === 'company') {
    const detailsToShow = settings.detailsToShow || ['name', 'description', 'title', 'department', 'company'];

    // Get company logo from organization (via user's organization)
    const logoUrl = (meetingType.user as any)?.organization?.logoUrl;

    // Check if this is multi-host mode (for company logo, we use simplified display like multi-host avatar mode)
    // For round_robin, ALWAYS use simplified mode (host is dynamically assigned, regardless of host count)
    // For service type, ALWAYS use simplified mode (only show company logo + company name)
    const isMultiHostCompanyMode = isRoundRobin || isServiceType || (isMultiHostMeeting && hasMultipleHosts);
    const selectedDepartment = settings.selectedDepartmentValue;

    // Helper function to check if a field should be shown and has value.
    // A per-meeting-type override counts as a present value so a customized field
    // still renders even when the host has no underlying value for it.
    const shouldShow = (field: string) => {
      // In multi-host company mode OR service type, name and title are not shown (simplified view)
      if (isMultiHostCompanyMode && (field === 'name' || field === 'title')) {
        return false;
      }
      // In multi-host company mode, department uses selectedDepartmentValue instead of toggle
      // For service type, department is never shown in company logo mode
      if (isMultiHostCompanyMode && field === 'department') {
        return false; // Handled separately below (but not for service types)
      }
      // Service type: only show company in company logo mode
      if (isServiceType && field !== 'company') {
        return false;
      }
      const overrideKey = (field === 'name' || field === 'title' || field === 'department' || field === 'company')
        ? field
        : null;
      if (field === 'company') {
        // Special handling for company field - check override, company and organizationName
        return detailsToShow.includes(field) && (hasOverride('company') || extendedHostData.company || extendedHostData.organizationName);
      }
      return detailsToShow.includes(field) && ((overrideKey && hasOverride(overrideKey)) || extendedHostData[field as keyof ExtendedHostData]);
    };

    // Get company name for fallback
    // For service types, ALWAYS use meeting type's organization (not provider's org)
    // Priority: organization.name > user.organizationName (organization.name is the actual org name)
    const companyName = isServiceType
      ? ((meetingType.user as any)?.organization?.name || meetingType.user?.organizationName)
      : ((meetingType.user as any)?.organization?.name || meetingType.user?.organizationName || extendedHostData.organizationName);

    // Override-applied values used in the render below (override wins over real value)
    const effName = applyOverride('name', extendedHostData.name);
    const effTitle = applyOverride('title', extendedHostData.jobTitle || extendedHostData.title);
    const effDepartment = applyOverride('department', extendedHostData.department);
    const effCompany = applyOverride('company', extendedHostData.organizationName || companyName);

    return (
      <div className={`flex items-center space-x-3 ${className}`}>
        {/* Company Logo */}
        <div className="flex-shrink-0">
          {logoUrl && logoUrl.trim() !== '' ? (
            <img
              src={logoUrl}
              alt={companyName ? `${companyName} logo` : 'Company logo'}
              className="h-12 w-12 rounded-lg object-contain bg-white"
              onError={(e) => {
                // Replace with fallback on error
                const parent = e.currentTarget.parentElement;
                if (parent) {
                  parent.innerHTML = `
                    <div class="h-12 w-12 rounded-lg bg-gray-100 flex items-center justify-center">
                      <svg class="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                  `;
                }
              }}
            />
          ) : (
            <div className="h-12 w-12 rounded-lg bg-gray-100 flex items-center justify-center">
              <svg className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
          )}
        </div>

        {/* Details Display */}
        <div className="flex-1">
          {/* For non-multi-host: show name if enabled */}
          {shouldShow('name') && (
            <h4 className="text-sm font-medium text-gray-900">
              {effName}
            </h4>
          )}

          {/* For non-multi-host: Job title and department on same line */}
          {!isMultiHostCompanyMode && (shouldShow('title') || shouldShow('department')) && (
            <div className="text-xs text-gray-600 mt-1">
              <span>
                {shouldShow('title') && effTitle}
                {shouldShow('title') && shouldShow('department') && effTitle && effDepartment && " • "}
                {shouldShow('department') && effDepartment}
              </span>
            </div>
          )}

          {/* For multi-host company mode: show selected department and/or company */}
          {/* For service type: only show company name (no department) */}
          {isMultiHostCompanyMode && ((!isServiceType && selectedDepartment) || shouldShow('company')) && (
            <div className="text-xs text-gray-600 mt-1">
              {!isServiceType && selectedDepartment && (
                <span>{selectedDepartment}</span>
              )}
              {!isServiceType && selectedDepartment && shouldShow('company') && (
                <span className="text-gray-400 mx-1">•</span>
              )}
              {shouldShow('company') && (
                <span className="text-gray-500">{effCompany}</span>
              )}
            </div>
          )}

          {/* For non-multi-host: Company on separate line with styling */}
          {!isMultiHostCompanyMode && shouldShow('company') && effCompany && (() => {
            // Check if company is the only detail showing
            const hasName = shouldShow('name');
            const hasTitle = shouldShow('title') && effTitle;
            const hasDepartment = shouldShow('department') && effDepartment;
            const isOnlyCompany = !hasName && !hasTitle && !hasDepartment;

            return (
              <div className={isOnlyCompany ? "text-base text-gray-900 mt-1" : "text-xs text-gray-500 mt-1"}>
                <span className={isOnlyCompany ? "font-semibold" : "px-2 py-1 bg-gray-100 rounded-md"}>
                  {effCompany}
                </span>
              </div>
            );
          })()}
        </div>
      </div>
    );
  }

  // Full host details display
  // Note: For round_robin, effectiveShowHostDetails is always 'company', so this won't run
  if (effectiveShowHostDetails === 'host') {
    const detailsToShow = settings.detailsToShow || ['name', 'description', 'title', 'department', 'company'];

    // For service type: use selectedProvider data if available
    // This shows the selected provider's info (avatar, displayName)
    // Note: Description/bio is NOT shown for service types
    const displayData = isServiceType && selectedProvider
      ? {
          name: selectedProvider.displayName,
          profilePicture: selectedProvider.avatarUrl || undefined,
          // For service types, bio/description is not shown
          bio: undefined,
          // For service types, title and department should never show
          jobTitle: undefined,
          title: undefined,
          department: undefined,
          // Company should ALWAYS be the meeting type's org, not provider's org
          // Priority: organization.name > user.organizationName
          company: undefined,
          organizationName: (meetingType.user as any)?.organization?.name || meetingType.user?.organizationName,
        }
      : extendedHostData;

    // Helper function to check if a field should be shown and has value.
    // A per-meeting-type override counts as a present value. Note: for service
    // types the NAME comes from the dynamically-selected provider, so the name
    // override is intentionally not applied (company override still is).
    const shouldShow = (field: string) => {
      // For service types: only show name and company
      // Hide title, department, and description
      if (isServiceType && (field === 'title' || field === 'department' || field === 'description')) {
        return false;
      }
      const overrideKey =
        field === 'company' ? 'company' as const
        : (!isServiceType && (field === 'name' || field === 'title' || field === 'department')) ? field
        : null;
      if (field === 'company') {
        // Special handling for company field - check override, company and organizationName
        return detailsToShow.includes(field) && (hasOverride('company') || displayData.company || displayData.organizationName);
      }
      if (field === 'description') {
        // Special handling for description field - check bio (not shown for service types)
        return detailsToShow.includes(field) && displayData.bio;
      }
      return detailsToShow.includes(field) && ((overrideKey && hasOverride(overrideKey)) || displayData[field as keyof typeof displayData]);
    };

    // Get company name - for service types, ALWAYS use meeting type's org
    // Priority: organization.name > user.organizationName
    const companyNameForHost = isServiceType
      ? ((meetingType.user as any)?.organization?.name || meetingType.user?.organizationName)
      : (displayData.company || displayData.organizationName);

    // Override-applied values used in the render below (override wins over real value).
    // Service-type name stays as the selected provider's name (no override).
    const effName = isServiceType ? displayData.name : applyOverride('name', displayData.name);
    const effTitle = applyOverride('title', displayData.jobTitle || displayData.title);
    const effDepartment = applyOverride('department', displayData.department);
    const effCompany = applyOverride('company', companyNameForHost);

    // For service type with selectedProvider, use provider's avatar
    const avatarUser = isServiceType && selectedProvider
      ? { name: selectedProvider.displayName, profilePicture: selectedProvider.avatarUrl || undefined }
      : hostData;

    return (
      <div className={`flex items-center space-x-3 ${className}`}>
        <UserAvatar
          user={avatarUser}
          size="lg"
          showProfilePicture={true}
        />
        <div className="flex-1">
          {shouldShow('name') && (
            <h4 className="text-sm font-medium text-gray-900">
              {effName}
            </h4>
          )}

          {/* Description/Bio - NOT shown for service types */}
          {shouldShow('description') && displayData.bio && (
            <p className="text-sm text-gray-600 mt-1 line-clamp-2">
              {displayData.bio}
            </p>
          )}

          {/* Job title and department on same line - NOT shown for service types */}
          {!isServiceType && (shouldShow('title') || shouldShow('department')) && (
            <div className="text-xs text-gray-600 mt-1">
              <span>
                {shouldShow('title') && effTitle}
                {shouldShow('title') && shouldShow('department') && effTitle && effDepartment && " • "}
                {shouldShow('department') && effDepartment}
              </span>
            </div>
          )}

          {/* Company on separate line with styling - Only show if toggle is enabled */}
          {/* For service types: shows meeting type's org, not provider's org */}
          {shouldShow('company') && effCompany && (() => {
            // Check if company is the only detail showing
            const hasName = shouldShow('name');
            // Description is never shown for service types
            const hasDescription = !isServiceType && shouldShow('description') && displayData.bio;
            const hasTitle = !isServiceType && shouldShow('title') && effTitle;
            const hasDepartment = !isServiceType && shouldShow('department') && effDepartment;
            const isOnlyCompany = !hasName && !hasDescription && !hasTitle && !hasDepartment;

            return (
              <div className={isOnlyCompany ? "text-base text-gray-900 mt-1" : "text-xs text-gray-500 mt-1"}>
                <span className={isOnlyCompany ? "font-semibold" : "px-2 py-1 bg-gray-100 rounded-md"}>
                  {effCompany}
                </span>
              </div>
            );
          })()}
        </div>
      </div>
    );
  }

  return null;
};

export default HostDisplay;